/**
 * Widget del gruppo "Da fare oggi": impegni, lead da richiamare, bozze degli
 * abbonamenti, abbonamenti in scadenza. Prendono il posto dei riquadri e dei
 * banner della vecchia pagina iniziale; quando non c'e' niente da fare lo
 * dicono, invece di sparire.
 */

import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { CalendarCheck, CheckCircle2, Coffee, PhoneCall, Repeat } from 'lucide-react';
import { cn } from '../../../lib/cn';
import { CATALOGO } from '../../../lib/dashboard/catalogo';
import { euroIntero } from '../../../lib/dashboard/formato';
import { abbonamentiInScadenza, leadDaRichiamare, type MotivoRichiamo } from '../../../lib/dashboard/kpiWidget';
import { formatSubscriptionPeriod } from '../../../lib/subscriptionInvoiceUtils';
import { useFonte, useOggi, useRicaricaFonte } from '../../../hooks/useFonte';
import { useImpegniOggi } from '../../../hooks/useImpegniOggi';
import { useLeadSettings } from '../../../store/useSettingsStore';
import type { Taglia } from '../../../lib/dashboard/catalogo';
import { RIGHE_ELENCO } from '../taglie';
import { Etichetta, Fonte, Iniziali, NumeroGrande, Pillola, Scheletro, StatoErrore, StatoVuoto } from './parti';
import type { PropsWidget } from './tipi';

const ora = (d: Date | string, tuttoIlGiorno: boolean) =>
  tuttoIlGiorno ? 'Tutto il giorno' : format(new Date(d), 'HH:mm', { locale: it });

/** Quante righe di elenco sotto il riepilogo, per taglia. */
const righeSotto = (taglia: Taglia) => (taglia === 'S' ? 0 : taglia === 'M' ? 1 : RIGHE_ELENCO[taglia] - 1);

function Altri({ quanti, className }: { quanti: number; className?: string }) {
  if (quanti <= 0) return null;
  return <p className={cn('mt-auto pt-1 text-[11px] font-medium opacity-60', className)}>e altri {quanti}</p>;
}

// ---------------------------------------------------------------------------

/** La data di oggi grande, come il calendario del telefono. */
function DataDiOggi({ compatta }: { compatta: boolean }) {
  const adesso = new Date();
  return (
    <div className={cn('flex flex-shrink-0 flex-col', compatta ? 'w-14' : 'w-20')}>
      <span className="text-[11px] font-bold uppercase tracking-wide text-primary-600">{format(adesso, compatta ? 'EEE' : 'EEEE', { locale: it })}</span>
      <span className={cn('font-display font-bold leading-none tracking-tight', compatta ? 'text-4xl' : 'text-6xl')}>{format(adesso, 'd')}</span>
      <span className="text-xs font-medium capitalize text-neutral-500">{format(adesso, 'MMMM', { locale: it })}</span>
    </div>
  );
}

export function WidgetImpegniOggi({ taglia, contesto }: PropsWidget<'impegni-oggi'>) {
  const { eventi, prossimo, caricamento, configurato, collegato, errore, ricarica } = useImpegniOggi();
  const puoAprireCalendario = CATALOGO['impegni-oggi'].apri?.ruoli.includes(contesto.ruolo) ?? false;
  const compatta = taglia === 'M';

  let destra;
  if (caricamento) destra = <Scheletro />;
  else if (!configurato) destra = <StatoVuoto compatto icona={CalendarCheck} testo="Google Calendar non è configurato" />;
  else if (!collegato) {
    destra = (
      <StatoVuoto
        compatto={compatta}
        icona={CalendarCheck}
        testo="Calendario non collegato"
        sotto="Collega il calendario aziendale per vedere qui gli impegni"
        azione={puoAprireCalendario && !compatta ? { etichetta: 'Collega', percorso: '/calendar' } : undefined}
      />
    );
  } else if (errore && eventi.length === 0) destra = <StatoErrore onRiprova={ricarica} />;
  else if (eventi.length === 0) destra = <StatoVuoto compatto={compatta} icona={Coffee} testo="Nessun impegno oggi" sotto="Giornata libera" />;
  else {
    const righe = compatta ? 1 : RIGHE_ELENCO[taglia];
    const elenco = compatta && prossimo ? [prossimo] : eventi.slice(0, righe);
    destra = (
      <div className="flex h-full min-h-0 flex-col">
        <ul className="min-h-0 space-y-1.5 overflow-hidden">
          {elenco.map((ev) => {
            const eIlProssimo = ev.id === prossimo?.id;
            return (
              <li key={ev.id} className={cn('flex items-stretch gap-2 rounded-xl px-2 py-1.5', eIlProssimo ? 'bg-primary-50' : 'bg-neutral-50')}>
                <span className={cn('w-1 flex-shrink-0 rounded-full', eIlProssimo ? 'bg-primary-500' : 'bg-neutral-300')} />
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold tabular-nums text-neutral-500">
                    {ora(ev.startDate, ev.allDay)}
                    {eIlProssimo && <span className="ml-1.5 text-primary-600">· prossimo</span>}
                  </p>
                  <p className="truncate text-xs font-semibold text-neutral-900">{ev.title}</p>
                </div>
              </li>
            );
          })}
        </ul>
        <Altri quanti={eventi.length - elenco.length} />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 gap-4">
      <DataDiOggi compatta={compatta} />
      <div className="min-h-0 min-w-0 flex-1">{destra}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------

const MOTIVI: Record<MotivoRichiamo, string> = { promemoria: 'Promemoria', azione: 'In ritardo', fermo: 'Fermo' };

export function WidgetLeadDaRichiamare({ taglia, impostazioni, contesto }: PropsWidget<'lead-da-richiamare'>) {
  const lead = useFonte('lead');
  const promemoria = useFonte('promemoriaLead');
  const ricarica = useRicaricaFonte();
  const { giorniRicontatto } = useLeadSettings();

  return (
    <Fonte stato={lead} onRiprova={() => ricarica('lead')}>
      {(tuttiLead) => (
        <Fonte stato={promemoria} onRiprova={() => ricarica('promemoriaLead')}>
          {(tuttiPromemoria) => {
            if (tuttiLead.length === 0) {
              return <StatoVuoto icona={PhoneCall} testo="Ancora nessun lead" sotto="Arriveranno da Meta, Google e dal sito" />;
            }
            const lista = leadDaRichiamare(tuttiLead, tuttiPromemoria, giorniRicontatto, Date.now(), impostazioni.soloMiei ? contesto.utenteId : null);
            if (lista.length === 0) return <StatoVuoto icona={CheckCircle2} testo="Tutti richiamati" sotto="Nessun lead in attesa" />;
            const righe = righeSotto(taglia);
            return (
              <div className="flex h-full min-h-0 flex-col gap-2">
                <div className="flex items-end gap-2">
                  <NumeroGrande valore={lista.length} taglia={righe ? 'S' : taglia} />
                  <Etichetta className="pb-0.5">da richiamare</Etichetta>
                </div>
                {righe > 0 && (
                  <ul className="min-h-0 space-y-1.5 overflow-hidden">
                    {lista.slice(0, righe).map((voce) => (
                      <li key={voce.leadId}>
                        <Link to={`/leads/${voce.leadId}`} className="flex items-center gap-2 rounded-2xl bg-white/[0.12] px-2 py-1.5 transition hover:bg-white/20">
                          <Iniziali nome={voce.nome} colore="bg-white/25 text-white" />
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-xs font-semibold">{voce.nome}</p>
                            <p className="truncate text-[11px] opacity-70">{voce.dettaglio}</p>
                          </div>
                          <Pillola className="flex-shrink-0">{MOTIVI[voce.motivo]}</Pillola>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <Altri quanti={lista.length - righe} />
              </div>
            );
          }}
        </Fonte>
      )}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

export function WidgetBozzeAbbonamenti({ taglia }: PropsWidget<'bozze-abbonamenti'>) {
  const bozze = useFonte('bozzeAbbonamenti');
  const ricarica = useRicaricaFonte();

  return (
    <Fonte stato={bozze} onRiprova={() => ricarica('bozzeAbbonamenti')}>
      {(tutte) => {
        if (tutte.length === 0) {
          return <StatoVuoto compatto={taglia !== 'L'} icona={CheckCircle2} testo="Tutto emesso" sotto="Nessuna fattura di abbonamento in attesa" />;
        }
        const totale = tutte.reduce((s, b) => s + (b.total_amount || 0), 0);
        const righe = righeSotto(taglia);
        return (
          <div className="flex h-full min-h-0 flex-col gap-2">
            <div className="flex items-end gap-2">
              <NumeroGrande valore={tutte.length} taglia="S" className="text-amber-700" />
              <span className="truncate pb-0.5 text-xs font-medium text-amber-900/70">da emettere</span>
              <Pillola colore="bg-amber-100 text-amber-800" className="mb-0.5 ml-auto flex-shrink-0">{euroIntero(totale)}</Pillola>
            </div>
            {righe > 0 && (
              <ul className="min-h-0 space-y-1.5 overflow-hidden">
                {tutte.slice(0, righe).map((b) => (
                  <li key={b.id} className="flex items-center gap-2 rounded-2xl bg-white/80 px-2 py-1.5 shadow-sm ring-1 ring-amber-100">
                    <Iniziali nome={b.customers?.company_name || 'Cliente'} colore="bg-amber-100 text-amber-800" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-semibold">{b.customers?.company_name || 'Cliente'}</p>
                      {b.subscription_period_start && b.subscription_period_end && (
                        <p className="truncate text-[11px] text-neutral-500">{formatSubscriptionPeriod({ start: b.subscription_period_start, end: b.subscription_period_end })}</p>
                      )}
                    </div>
                    <span className="flex-shrink-0 text-xs font-semibold tabular-nums">{euroIntero(b.total_amount || 0)}</span>
                  </li>
                ))}
              </ul>
            )}
            <Altri quanti={tutte.length - righe} />
          </div>
        );
      }}
    </Fonte>
  );
}

// ---------------------------------------------------------------------------

export function WidgetRinnoviAbbonamenti({ taglia }: PropsWidget<'rinnovi-abbonamenti'>) {
  const abbonamenti = useFonte('abbonamenti');
  const ricarica = useRicaricaFonte();
  const oggi = useOggi();

  return (
    <Fonte stato={abbonamenti} onRiprova={() => ricarica('abbonamenti')}>
      {(tutti) => {
        if (tutti.length === 0) {
          return <StatoVuoto compatto={taglia !== 'L'} icona={Repeat} testo="Ancora nessun abbonamento" sotto="I rinnovi in arrivo compariranno qui" />;
        }
        const lista = abbonamentiInScadenza(tutti, oggi);
        if (lista.length === 0) return <StatoVuoto compatto={taglia !== 'L'} icona={CalendarCheck} testo="Nessun rinnovo in vista" sotto="Nei prossimi 30 giorni" />;
        const scaduti = lista.filter((a) => a.statoRinnovo === 'expired').length;
        const righe = righeSotto(taglia);
        return (
          <div className="flex h-full min-h-0 flex-col gap-2">
            <div className="flex items-end gap-2">
              <NumeroGrande valore={lista.length} taglia="S" className={scaduti ? 'text-red-600' : 'text-orange-600'} />
              <span className="truncate pb-0.5 text-xs font-medium text-neutral-600">
                {scaduti ? `in scadenza, ${scaduti} già scaduti` : 'in scadenza entro 30 giorni'}
              </span>
            </div>
            {righe > 0 && (
              <ul className="min-h-0 space-y-1.5 overflow-hidden">
                {lista.slice(0, righe).map((a) => (
                  <li key={a.id} className="flex items-center gap-2 rounded-2xl bg-white/80 px-2 py-1.5 shadow-sm ring-1 ring-orange-100">
                    <Iniziali nome={a.customers?.company_name || 'Cliente'} colore="bg-orange-100 text-orange-700" />
                    <span className="min-w-0 flex-1 truncate text-xs font-semibold">{a.customers?.company_name || 'Cliente'}</span>
                    <Pillola colore={a.giorni < 0 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'} className="flex-shrink-0">
                      {a.giorni < 0 ? `scaduto da ${-a.giorni} gg` : a.giorni === 0 ? 'oggi' : `tra ${a.giorni} gg`}
                    </Pillola>
                  </li>
                ))}
              </ul>
            )}
            <Altri quanti={lista.length - righe} />
          </div>
        );
      }}
    </Fonte>
  );
}
